import { ILogin, IToken, ProfileType } from 'data/interfaces/IRegistration';
import { IStats, IUser, IUserProfile } from 'data/interfaces/IUser';
import { IncomingMessage, ServerResponse } from 'http';
import { InterfaceLanguage } from 'types/enums';
import { IPagination } from 'types/types';
import request from 'utils/request';

export default class UserRepository {
  static async getCurrentUser(context?: {
    req?: IncomingMessage;
    res?: ServerResponse;
  }): Promise<IUser> {
    try {
      const cookieHeader = context?.req?.headers.cookie || '';

      const res = await fetch(process.env.HOST_ROOT + '/api/profile', {
        headers: cookieHeader != '' ? { Cookie: cookieHeader } : undefined,
        credentials: 'include',
      });

      const data = await res.json();
      context?.res?.setHeader('Set-Cookie', res.headers.get('set-cookie') || '');

      if (data?.err || !data) {
        return null;
      }
      const profile = { ...data.profile, profileId: data.profile.id };
      return {
        ...profile,
        profiles: [profile],
        isRegistrationCompleted: true,
      };
    } catch (e) {
      console.error('Error fetching current user:', e);
      return null;
    }
  }

  static async editCurrentUser(
    profileId: number,
    data: Partial<Pick<IUser, 'email' | 'phone' | 'language' | 'firstName' | 'lastName'>>,
  ): Promise<any | null> {
    const res = await request({
      method: 'patch',
      url: `/api/profiles/${profileId}`,
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async editUserLanguage(
    profileId: number,
    data: { lang: InterfaceLanguage },
  ): Promise<any | null> {
    const res = await request({
      method: 'patch',
      url: `/api/profiles/${profileId}`,
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async loginByEmailOrPhone(data: ILogin): Promise<IToken | null> {
    const res = await request({
      method: 'post',
      url: '/api/auth/login',
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getBlackListedProfiles(): Promise<IPagination<IUserProfile> | null> {
    const res = await request({
      method: 'get',
      url: '/api/profile/blackList',
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async addProfileToBlackList(profileId: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/profile${profileId}/blackList`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async removeProfileFromBlackList(profileId: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/profile/${profileId}/removeFromBlackList`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getStats(token?: string, profile?: ProfileType): Promise<IStats | null> {
    const res = await request({
      method: 'get',
      url: '/api/stats',
      token,
      profile,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async editProfileByType(
    data: Partial<IUserProfile>,
    profile?: ProfileType,
  ): Promise<IUserProfile | null> {
    const res = await request({
      method: 'patch',
      url: `/api/profile/${profile}`,
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getUserProfileById(
    id: number,
    token?: string,
    profile?: ProfileType,
  ): Promise<IUserProfile | null> {
    const res = await request({
      method: 'get',
      url: `/api/profile/${id}`,
      token,
      profile,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async removeCurrentUserByType(profile: ProfileType): Promise<any | null> {
    const res = await request({
      method: 'delete',
      url: `/api/profile/${profile}`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getBackgrounds(data?: {
    profileId?: number;
    categoryId?: number;
    lang?: string;
  }): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: '/api/background',
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }
}
